import React, { useCallback, useContext, useEffect, useState } from "react";
import {
    Box, Grid, Paper, Typography, AppBar, Tabs, Tab, useTheme,
    Button, Stack, Card, InputAdornment, Container,
} from "@mui/material";
import { Pie, Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import Pin from "../../assets/images/pin.svg";
import booked_shift from "../../assets/images/booked_shift.svg";
import available_shift from "../../assets/images/available_shift.svg";
import candidate_active from "../../assets/images/candidate_active.svg";
import candidate_not_active from "../../assets/images/candidate_not_active.svg";
import canHasNoShift from "../../assets/images/canHasNoShift.svg";
import {
    CheckCircle, XCircle, Check, CalendarCheck, Clock,
    DollarSign, UserCheck, UserMinus, Calendar, Filter,
    TrendingUp, Users, Briefcase, MapPin
} from "lucide-react";
import PropTypes from "prop-types";
import axios from "axios";
import apiConfigs from "../../config/config";
import CountUp from "react-countup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { customEnLocale } from "../../helper/customLocale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { makeStyles, TextField } from "@material-ui/core";
import { GoogleMap, LoadScript, Marker, InfoWindow, MarkerClustererF } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { ThemeContext } from '../../App';
import MapsMarker from "./MapsMarker";
import GoogleMapInfo from "./GoogleMapInfo";
import { Suspense } from "react";
import Loading from "../../components/Loading/Loading";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        padding: theme.spacing(3),
    },
    tabRoot: {
        color: theme.palette.listItem,
    },
    metricCard: {
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, var(--card-color), transparent)',
        }
    }
}));

ChartJS.register(CategoryScale, LinearScale, ArcElement, BarElement, Title, Tooltip, Legend);

// TabPanel Component
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const containerStyle = {
    width: "100%",
    height: "100%"
};

// Enhanced Metric Card Component
const MetricCard = ({ title, value, icon, color, delay = 0 }) => {
    const { mode } = useContext(ThemeContext);

    return (
        <Paper
            elevation={2}
            sx={{
                p: 3,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: mode === "light"
                    ? `linear-gradient(135deg, ${color}15, ${color}05)`
                    : `linear-gradient(135deg, ${color}30, ${color}15)`,
                color: mode === "light" ? color : "#fff",
                height: 140,
                textAlign: "left",
                transition: "all 0.3s ease",
                cursor: "pointer",
                boxShadow: mode === "light"
                    ? "0 4px 12px rgba(0,0,0,0.08)"
                    : "0 4px 20px rgba(0,0,0,0.25)",
                border: `1px solid ${mode === "light" ? `${color}20` : `${color}30`}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${color}, transparent)`,
                },
                "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: mode === "light"
                        ? "0 12px 28px rgba(0,0,0,0.15)"
                        : "0 12px 32px rgba(0,0,0,0.4)",
                },
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        opacity: 0.8,
                        mb: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem'
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', md: '2rem' }
                    }}
                >
                    <CountUp delay={delay} end={value} duration={2.5} />
                </Typography>
            </Box>

            <Box
                sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: mode === "light"
                        ? `${color}20`
                        : `${color}30`,
                    boxShadow: `0 4px 12px ${color}40`,
                    ml: 2,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
        </Paper>
    );
};

// Enhanced Chart Container Component
const ChartContainer = ({ title, children, height = 300 }) => {
    const { mode } = useContext(ThemeContext);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                height: "auto",
                background: mode === "light" ? "#ffffff" : "#303640",
                border: mode === "light" ? "1px solid #e0e0e0" : "1px solid hsl(218.82deg 20.48% 16.27%)",
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: mode === "light"
                        ? '0 8px 24px rgba(0,0,0,0.12)'
                        : '0 8px 24px rgba(0,0,0,0.3)',
                }
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: mode === "light" ? "text.primary" : "#e0e0e0",
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                <TrendingUp size={20} />
                {title}
            </Typography>
            <Box sx={{ height }}>
                {children}
            </Box>
        </Paper>
    );
};

// Dashboard Component
export default function Dashboard() {
    const classes = useStyles();
    const host = window.location.host;
    const [selected, setSelected] = React.useState(null);
    const [selected1, setSelected1] = React.useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [candidateActive, setCandidateActive] = useState(null);
    const [candidateHasNoShift, setCandidateHasNoShift] = useState(null);
    const [candidateNotActive, setCandidateNotActive] = useState(null);
    const [selectAvailable, setAvailable] = useState(null);
    const theme = useTheme();
    const [value, setValue] = useState(0);
    const [bookingDashboard, setBookingDashboard] = useState({});
    const [date, setDate] = useState({
        start_date: new Date(new Date().setMonth(new Date().getMonth() - 6)),
        end_date: new Date()
    });
    const [loading, setLoading] = useState(false);

    const DashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiConfigs.API_URL}api/booking/dashboard`, {
                headers: { "Content-Type": "application/json" },
                params: {
                    start_date: date?.start_date,
                    end_date: date?.end_date
                }
            });
            setBookingDashboard(response.data || {});
        } catch (error) {
            console.error("Dashboard fetch error:", error);
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => {
        DashboardData();
    }, [date]);

    const handleChange = (event, newValue) => setValue(newValue);

    const dateHandler = (name, date) => {
        setDate(prev => ({ ...prev, [name]: date }));
    };

    const handleClearFilter = () => {
        setDate({
            start_date: new Date(new Date().setMonth(new Date().getMonth() - 6)),
            end_date: new Date()
        });
    };

    // Theme-based palette
    const palette = {
        blue: theme.palette.primary.main,
        green: theme.palette.success?.main || "#66BB6A",
        amber: theme.palette.secondary.main,
        purple: theme.palette.primary.light,
        red: theme.palette.error?.main || "#EF5350",
        gray: theme.palette.grey[500],
    };

    const dashboard = bookingDashboard?.data || {};
    const status1 = dashboard?.status_count || {};
    const status = dashboard?.status || {};
    const speciality = dashboard?.top_speciality || {};
    const customerWiseBooking = dashboard?.customerWiseBooking || {};
    const candStatus = dashboard?.candidateStatus || {};
    const canCat = dashboard?.canCategory || {};
    const brandWiseBooking = dashboard?.gradeWiseBooking || {};
    const paymethod = dashboard?.payrolTypeCategory || {};
    const specialityCandidate = dashboard?.specialityCandidate || {};
    const CandidateTag = dashboard?.CandidateTag || [];
    const site = dashboard?.site || [];
    const booked = dashboard?.booked_shift || [];
    const available = dashboard?.available_shift || [];
    const candidateActives = dashboard?.CandidateActive || [];
    const candidateNotActives = dashboard?.CandidateNotActive || [];
    const candidateNotAvailableForShift = dashboard?.CandidateNotAvailableForShift || [];
    // Booking Metrics Cards
    const bookingMetrics = [
        { title: "Vacancies", value: parseInt(status1?.vacancy || 0), icon: <Briefcase />, color: palette.blue },
        { title: "Booked", value: parseInt(status1?.booked || 0), icon: <CalendarCheck />, color: palette.green },
        { title: "Provisional", value: parseInt(status1?.provisional || 0), icon: <DollarSign />, color: palette.amber },
        { title: "Completed", value: parseInt(status1?.completed || 0), icon: <Check />, color: palette.purple },
        { title: "Cancelled", value: parseInt(status1?.cancel || 0), icon: <XCircle />, color: palette.red },
    ];

    const statusColorMap = {
        'vacancy': palette.blue,
        'booked': palette.green,
        'provisional': palette.amber,
        'completed': palette.purple,
        'cancel': palette.red
    };

    const bgColor = Object.keys(status1).map(item => statusColorMap[item]);
    const center = { lat: 51.509865, lng: -0.118092 };
    const bookingLabel = ["Vacancies", "Booked", "Provisional", "Completed", "Cancelled"];

    // Charts Data
    const bookingData = {
        labels: bookingLabel,
        datasets: [
            {
                label: "Bookings",
                data: Object.values(status1),
                backgroundColor: bgColor,
                borderColor: theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    };

    const bookingSpecialityData = {
        labels: Object.keys(speciality),
        datasets: [
            {
                label: "Bookings",
                data: Object.values(speciality),
                backgroundColor: [palette.blue, palette.green, palette.amber, palette.red, palette.purple],
                borderRadius: 4,
                borderWidth: 0,
            }
        ],
    };

    const customerWiseBookings = {
        labels: Object.keys(customerWiseBooking),
        datasets: [
            {
                label: "Customer Wise Booking",
                data: Object.values(customerWiseBooking),
                backgroundColor: [palette.blue, palette.green, palette.amber, palette.red, palette.purple],
                borderRadius: 4,
            }
        ],
    };

    const brandBooking = {
        labels: Object.keys(brandWiseBooking),
        datasets: [
            {
                label: 'Bookings',
                data: Object.values(brandWiseBooking),
                backgroundColor: [
                    'rgba(74, 107, 255, 0.8)',
                    'rgba(255, 94, 77, 0.8)',
                    'rgba(46, 196, 182, 0.8)',
                    'rgba(255, 173, 61, 0.8)',
                    'rgba(156, 105, 226, 0.8)',
                    'rgba(72, 207, 173, 0.8)',
                ],
                borderColor: [
                    'rgba(74, 107, 255, 1)',
                    'rgba(255, 94, 77, 1)',
                    'rgba(46, 196, 182, 1)',
                    'rgba(255, 173, 61, 1)',
                    'rgba(156, 105, 226, 1)',
                    'rgba(72, 207, 173, 1)',
                ],
                borderWidth: 2,
            },
        ],
    };

    // Candidate Status Data
    const keyMap = {
        "Active": "active",
        "Not active": "Inactive",
        "Pre reg": "prereg",
        "New sign up": "newuser",
        "Archive": "archive",
        "Do not use": "restricted"
    };
    const expectedLabels = Object.values(keyMap);

    const renamedData = Object.fromEntries(
        expectedLabels.map(label => {
            const originalKey = Object.keys(keyMap).find(key => keyMap[key] === label);
            const value = originalKey && candStatus[originalKey] ? candStatus[originalKey] : 0;
            return [label, value];
        })
    );

    const candidateMetrics = [
        { title: "Active", value: parseInt(renamedData?.active || 0), icon: <UserCheck />, color: palette.green },
        { title: "Not Active", value: parseInt(renamedData?.Inactive || 0), icon: <XCircle />, color: palette.red },
        { title: "Pre Reg", value: parseInt(renamedData?.prereg || 0), icon: <Clock />, color: palette.amber },
        { title: "New Sign Up", value: parseInt(renamedData?.newuser || 0), icon: <CheckCircle />, color: palette.blue },
        { title: "Archive", value: parseInt(renamedData?.archive || 0), icon: <CalendarCheck />, color: palette.purple },
        { title: "Do Not Use", value: parseInt(renamedData?.restricted || 0), icon: <UserMinus />, color: palette.gray },
    ];

    const candidateStatusData = {
        labels: Object.keys(renamedData),
        datasets: [
            {
                label: "Candidates",
                data: Object.values(renamedData),
                backgroundColor: [palette.green, palette.red, palette.amber, palette.blue, palette.purple, palette.gray],
                borderColor: theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    };

    const candidateCategoryData = {
        labels: Object.keys(canCat),
        datasets: [
            {
                label: "Category",
                data: Object.values(canCat),
                backgroundColor: [palette.red, palette.green, palette.blue, palette.amber, palette.purple],
                borderRadius: 4,
            }
        ],
    };

    const paymentMethodWiseData = {
        labels: Object.keys(paymethod),
        datasets: [
            {
                label: "Bookings",
                data: Object.values(paymethod),
                backgroundColor: [palette.blue, palette.green],
                borderColor: theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    };

    const specialityWiseCandidate = {
        labels: Object.keys(specialityCandidate),
        datasets: [
            {
                label: "Speciality Wise Candidate",
                data: Object.values(specialityCandidate),
                backgroundColor: palette.blue,
                borderRadius: 4,
            }
        ],
    };

    const { mode } = useContext(ThemeContext);

    const chartOptions = {
        maintainAspectRatio: false,
        animation: {
            duration: 2000,
            easing: 'easeOutQuart'
        },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: theme.palette.listItem,
                    font: {
                        size: 12,
                    },
                },
            },
            tooltip: {
                backgroundColor: mode === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.8)',
                titleColor: mode === 'light' ? '#333' : '#fff',
                bodyColor: mode === 'light' ? '#666' : '#ccc',
                borderColor: mode === 'light' ? '#e0e0e0' : '#333',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
            },
        },
    };

    const BookingMark = useCallback(() => {
        return [
            ...(candidateNotAvailableForShift?.map(user => ({ ...user, type: 'candidateNoShift', icon: canHasNoShift, onClick: setCandidateHasNoShift })) || []),
            ...(candidateActives?.map(user => ({ ...user, type: 'candidateActive', icon: candidate_active, onClick: setCandidateActive })) || []),
            ...(candidateNotActives?.map(user => ({ ...user, type: 'candidateNotActive', icon: candidate_not_active, onClick: setCandidateNotActive })) || []),
            ...(available?.map(user => ({ ...user, type: 'available', icon: available_shift, onClick: setAvailable })) || []),
            ...(booked?.map(user => ({ ...user, type: 'booked', icon: booked_shift, onClick: setSelectedBooking })) || [])
        ]
    }, [candidateNotAvailableForShift, candidateActives, candidateNotActives, available, booked]);
    const CandidateMark = useCallback(() => {
        //         CandidateTag
        // site
        return [
            ...(CandidateTag?.map(can => ({ ...can, icon: Pin, onClick: setSelected, isCandidate: true })) || []),
            ...(site?.map(Sites => ({ ...Sites, icon: undefined, onClick: setSelected1 })) || [])
        ]
    }, [CandidateTag, site]);
    const BookingInfo = useCallback(() => {
        return [
            { data: candidateHasNoShift, setter: setCandidateHasNoShift, isCandidate: true },
            { data: candidateNotActive, setter: setCandidateNotActive, isCandidate: true },
            { data: candidateActive, setter: setCandidateActive, isCandidate: true },
            { data: selectAvailable, setter: setAvailable },
            { data: selectedBooking, setter: setSelectedBooking }
        ].filter(item => item.data !== null)
    }, [candidateHasNoShift, candidateNotActive, candidateActive, selectAvailable, selectedBooking])
    const CandidateInfo = useCallback(() => {
        return [
            { data: selected1, setter: setSelected1 },
            { data: selected, setter: setSelected, isCandidate: true }
        ].filter(item => item.data !== null)
    }, [selected1, selected]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header with Tabs and Filters */}
            <AppBar
                className={classes.appbar}
                position="static"
                sx={{
                    // background: theme.palette.drawer,
                    px: 0,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    "&.MuiPaper-root": {
                        backgroundColor: mode == "light" ? "#ffffff" : "#303640",
                        color: mode == "light" ? "#515359" : "#b6b7b9",
                    },


                }}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    // gap: 3,
                    // py: 2,
                    // px: 3,
                    paddingLeft: 0,
                    padding: "8px",
                }}>
                    {/* Enhanced Tabs */}
                    <Tabs
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        value={value}
                        onChange={handleChange}
                        sx={{
                            minHeight: 'auto',
                            '& .MuiTab-root': {
                                // minHeight: 'auto',
                                // py: 1.5,
                                // px: 3,
                                // fontSize: '0.95rem',
                                // fontWeight: 600,
                                color: mode == "light" ? "#515359" : "#b6b7b9",
                                // borderRadius: 1,
                                // marginRight: 1,
                                // transition: 'all 0.3s ease',
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                },
                            },
                            // '& .MuiTabs-indicator': {
                            //     height: 3,
                            //     borderRadius: 2,
                            //     boxShadow: '0 2px 8px rgba(255,255,255,0.3)',
                            // }
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: "none" }}>
                                    {/* <Briefcase size={18} /> */}
                                    Booking
                                </Box>
                            }
                            {...a11yProps(0)}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: "none" }}>
                                    {/* <Users size={18} /> */}
                                    Candidate
                                </Box>
                            }
                            {...a11yProps(1)}
                        />
                    </Tabs>

                    {/* Date Filters - Updated to match */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            borderRadius: 2,
                            p: 1,
                            backdropFilter: 'blur(10px)',
                        }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={customEnLocale}>
                                <DatePicker
                                    label="Start Date"
                                    value={date.start_date}
                                    onChange={(newValue) => dateHandler("start_date", newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            variant="outlined"
                                            sx={{
                                                width: 150,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1.5,
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                }
                                            }}
                                        />
                                    )}
                                    inputFormat="dd-MMM-yyyy"
                                />
                            </LocalizationProvider>

                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={customEnLocale}>
                                <DatePicker
                                    label="End Date"
                                    value={date.end_date}
                                    onChange={(newValue) => dateHandler("end_date", newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            variant="outlined"
                                            sx={{
                                                width: 150,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1.5,
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    inputFormat="dd-MMM-yyyy"
                                />
                            </LocalizationProvider>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleClearFilter}
                            startIcon={<Filter size={16} />}
                            sx={{
                                height: '40px',
                                minWidth: 100,
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'none',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: mode == "light" ? "#515359" : "#b6b7b9",
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(255,255,255,0.2)',
                                }
                            }}
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>
            </AppBar>

            {/* Loading State */}
            {
                loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography>Loading dashboard ...</Typography>
                    </Box>
                )
            }

            {/* Booking Dashboard */}
            {loading == false && <TabPanel value={value} index={0}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 4,
                        color: mode === "light" ? "text.primary" : "#fff",
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Briefcase size={28} />
                    Booking Dashboard
                </Typography>

                {/* Metrics Grid */}
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    {bookingMetrics.map((metric, idx) => (
                        <Grid item key={idx} xs={12} sm={6} md={4} lg={2.4}>
                            <MetricCard {...metric} delay={idx * 0.2} />
                        </Grid>
                    ))}
                </Grid>

                {/* Charts Grid */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Status Distribution" height={320}>
                            <Pie
                                data={bookingData}
                                options={{
                                    ...chartOptions,
                                    plugins: {
                                        ...chartOptions.plugins,
                                        tooltip: {
                                            ...chartOptions.plugins.tooltip,
                                            callbacks: {
                                                label: (context) => `${context.label}: ${context.raw}`
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Speciality-wise Bookings" height={320}>
                            <Bar
                                data={bookingSpecialityData}
                                options={{
                                    ...chartOptions,
                                    responsive: true,
                                    scales: {
                                        x: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : theme.palette.listItem
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        },
                                        y: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Customer-wise Bookings" height={320}>
                            <Bar
                                data={customerWiseBookings}
                                options={{
                                    ...chartOptions,
                                    indexAxis: 'y',
                                    scales: {
                                        x: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        },
                                        y: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Brand-wise Bookings" height={320}>
                            <Doughnut
                                data={brandBooking}
                                options={chartOptions}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12}>
                        <ChartContainer title="Bookings Geographical Distribution" height={500}>
                            <Box sx={{ height: "100%", width: "100%" }}>
                                <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={7}>
                                    {/* Single MarkerClusterer for ALL markers */}
                                    <Suspense fallback={<Loading />}>
                                        {BookingMark && <MapsMarker data={BookingMark()} />}
                                    </Suspense>
                                    {BookingInfo() && <GoogleMapInfo googleInfo={BookingInfo()} />}
                                    {/* InfoWindows */}
                                    {/* {[
                                        { data: candidateHasNoShift, setter: setCandidateHasNoShift },
                                        { data: candidateNotActive, setter: setCandidateNotActive },
                                        { data: candidateActive, setter: setCandidateActive },
                                        { data: selectAvailable, setter: setAvailable },
                                        { data: selectedBooking, setter: setSelectedBooking },
                                        { data: selected, setter: setSelected, isCandidate: true },
                                    ].map((info, idx) =>
                                        info.data ? (
                                            <InfoWindow
                                                key={`info-${idx}-${info.data.id}`}
                                                position={{
                                                    lat: parseFloat(info.data.latitude) || 0,
                                                    lng: parseFloat(info.data.longitude) || 0
                                                }}
                                                options={{
                                                    disableAutoPan: false,
                                                    pixelOffset: new window.google.maps.Size(0, -23) // Adjusts position above marker
                                                }}
                                                onCloseClick={() => info.setter(null)}
                                            >
                                                <Box
                                                    sx={{
                                                        backgroundColor: "#fff",
                                                        borderRadius: 2,
                                                        p: 2,
                                                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                                        minWidth: info.isCandidate ? 220 : 200,
                                                        maxWidth: info.isCandidate ? 280 : 260,
                                                        fontFamily: "Roboto, sans-serif",
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: info.isCandidate ? "#1976d2" : "#1E3A8A",
                                                            mb: 1,
                                                            borderBottom: info.isCandidate ? "none" : "2px solid #E0E7FF",
                                                            pb: info.isCandidate ? 0 : 0.5,
                                                        }}
                                                    >
                                                        {info.isCandidate ? `👤 ${info.data.name}` : `🏢 ${info.data.name || "N/A"}`}
                                                    </Typography>

                                                    {info.isCandidate ? (
                                                        <>
                                                            {info.data.email && (
                                                                <Typography variant="body2" sx={{ color: "#374151", mb: 0.5, display: "flex", alignItems: "center" }}>
                                                                    📧 <span style={{ marginLeft: 6 }}>{info.data.email}</span>
                                                                </Typography>
                                                            )}
                                                            {info.data.contact_number && (
                                                                <Typography variant="body2" sx={{ color: "#374151", mb: 1, display: "flex", alignItems: "center" }}>
                                                                    📞 <span style={{ marginLeft: 6 }}>{info.data.contact_number}</span>
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                                                                <a
                                                                    href={`/admin/candidate/${info.data.id}/detail`}
                                                                    style={{
                                                                        backgroundColor: "#1976d2",
                                                                        color: "#fff",
                                                                        textDecoration: "none",
                                                                        padding: "6px 14px",
                                                                        borderRadius: "8px",
                                                                        fontWeight: 600,
                                                                        fontSize: "0.9rem",
                                                                        transition: "0.3s",
                                                                    }}
                                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#115293")}
                                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1976d2")}
                                                                >
                                                                    View Profile →
                                                                </a>
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography variant="body1" sx={{ color: "#374151", fontWeight: 500, mb: 0.5 }}>
                                                                Customer: <span style={{ color: "#111827", fontWeight: 600 }}>{info.data.customer_name || "N/A"}</span>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                                                                Total: <span style={{ color: "#2563EB", fontWeight: 700 }}>{info.data.total || 0}</span>
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </InfoWindow>
                                        ) : null
                                    )} */}
                                </GoogleMap>
                            </Box>
                        </ChartContainer>
                    </Grid>
                </Grid>
            </TabPanel>}

            {/* Candidate Dashboard */}
            {loading == false && <TabPanel value={value} index={1}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 4,
                        color: mode === "light" ? "text.primary" : "#fff",
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Users size={28} />
                    Candidate Dashboard
                </Typography>

                {/* Metrics Grid */}
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    {candidateMetrics.map((metric, idx) => (
                        <Grid item key={idx} xs={12} sm={6} md={4} lg={2}>
                            <MetricCard {...metric} delay={idx * 0.15} />
                        </Grid>
                    ))}
                </Grid>

                {/* Charts Grid */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Candidate Status Distribution" height={320}>
                            <Pie
                                data={candidateStatusData}
                                options={{
                                    ...chartOptions,
                                    plugins: {
                                        ...chartOptions.plugins,
                                        tooltip: {
                                            ...chartOptions.plugins.tooltip,
                                            callbacks: {
                                                label: (context) => `${context.label}: ${context.raw}`
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Category-wise Candidates" height={320}>
                            <Bar
                                data={candidateCategoryData}
                                options={{
                                    ...chartOptions,
                                    scales: {
                                        x: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        },
                                        y: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Payment Method Distribution" height={320}>
                            <Pie
                                data={paymentMethodWiseData}
                                options={chartOptions}
                            />
                        </ChartContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartContainer title="Speciality-wise Candidates" height={320}>
                            <Bar
                                data={specialityWiseCandidate}
                                options={{
                                    ...chartOptions,
                                    indexAxis: 'y',
                                    scales: {
                                        x: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        },
                                        y: {
                                            grid: {
                                                color: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                                            },
                                            ticks: {
                                                color: theme.palette.listItem
                                            }
                                        }
                                    }
                                }}
                            />
                        </ChartContainer>
                    </Grid>

                    {/* Map Section */}
                    <Grid item xs={12}>
                        <ChartContainer title="Candidate Geographical Distribution" height={500}>
                            <Box sx={{ height: "100%", width: "100%" }}>
                                <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={7}>
                                    {/* Candidate Markers */}
                                    <Suspense fallback={<Loading />}>
                                        {CandidateMark && <MapsMarker data={CandidateMark()} />}
                                    </Suspense>
                                    {CandidateInfo && <GoogleMapInfo googleInfo={CandidateInfo()} />}

                                    {/* {CandidateTag.map((user) => (
                                        <Marker
                                            key={user.id}
                                            position={{
                                                lat: parseFloat(user.latitude),
                                                lng: parseFloat(user.longitude),
                                            }}
                                            icon={{
                                                url: Pin,
                                                scaledSize: new window.google.maps.Size(45, 35),
                                            }}
                                            onClick={() => setSelected(user)}
                                        />
                                    ))} */}
                                    {/* Site Markers */}
                                    {/* {site.map((user) => (
                                        <Marker
                                            key={user.id}
                                            position={{
                                                lat: parseFloat(user.latitude),
                                                lng: parseFloat(user.longitude),
                                            }}
                                            onClick={() => setSelected1(user)}
                                        />
                                    ))} */}

                                    {/* --- Site InfoWindow --- */}
                                    {/* {selected1 && (
                                        <InfoWindow
                                            position={{
                                                lat: parseFloat(selected1.latitude),
                                                lng: parseFloat(selected1.longitude),
                                            }}
                                            onCloseClick={() => setSelected1(null)}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: "#ffffff",
                                                    borderRadius: 2,
                                                    p: 2,
                                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                                    minWidth: 200,
                                                    maxWidth: 260,
                                                    fontFamily: "Roboto, sans-serif",
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "#1E3A8A",
                                                        mb: 1,
                                                        borderBottom: "2px solid #E0E7FF",
                                                        pb: 0.5,
                                                    }}
                                                >
                                                    🏢 {selected1?.name || "N/A"}
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ color: "#374151", fontWeight: 500, mb: 0.5 }}
                                                >
                                                    Customer:{" "}
                                                    <span style={{ color: "#111827", fontWeight: 600 }}>
                                                        {selected1?.customer_name || "N/A"}
                                                    </span>
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "#4B5563", fontWeight: 500 }}
                                                >
                                                    Total:{" "}
                                                    <span style={{ color: "#2563EB", fontWeight: 700 }}>
                                                        {selected1?.total || 0}
                                                    </span>
                                                </Typography>
                                            </Box>
                                        </InfoWindow>
                                    )} */}

                                    {/* --- Candidate InfoWindow --- */}
                                    {/* {selected && (
                                        <InfoWindow
                                            position={{
                                                lat: parseFloat(selected.latitude),
                                                lng: parseFloat(selected.longitude),
                                            }}
                                            onCloseClick={() => setSelected(null)}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: "#ffffff",
                                                    p: 2,
                                                    borderRadius: 2,
                                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                                    minWidth: 220,
                                                    maxWidth: 280,
                                                    fontFamily: "Roboto, sans-serif",
                                                    textAlign: "left", // 👈 left-aligned content
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "#1976d2",
                                                        mb: 1,
                                                    }}
                                                >
                                                    👤 {selected.name}
                                                </Typography>

                                                {selected.email && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: "#374151", mb: 0.5, display: "flex", alignItems: "center" }}
                                                    >
                                                        📧 <span style={{ marginLeft: 6 }}>{selected.email}</span>
                                                    </Typography>
                                                )}

                                                {selected.contact_number && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: "#374151", mb: 1, display: "flex", alignItems: "center" }}
                                                    >
                                                        📞 <span style={{ marginLeft: 6 }}>{selected.contact_number}</span>
                                                    </Typography>
                                                )}

                                                <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                                                    <a
                                                        href={`/admin/candidate/${selected.id}/detail`}
                                                        style={{
                                                            backgroundColor: "#1976d2",
                                                            color: "#fff",
                                                            textDecoration: "none",
                                                            padding: "6px 14px",
                                                            borderRadius: "8px",
                                                            fontWeight: 600,
                                                            fontSize: "0.9rem",
                                                            transition: "0.3s",
                                                        }}
                                                        onMouseEnter={(e) =>
                                                            (e.currentTarget.style.backgroundColor = "#115293")
                                                        }
                                                        onMouseLeave={(e) =>
                                                            (e.currentTarget.style.backgroundColor = "#1976d2")
                                                        }
                                                    >
                                                        View Profile →
                                                    </a>
                                                </Box>
                                            </Box>
                                        </InfoWindow>
                                    )} */}
                                </GoogleMap>
                            </Box>
                        </ChartContainer>
                    </Grid>
                </Grid>
            </TabPanel>}
        </Container >
    );
}